// Exporting a map — the one pipeline the cockpit's sheets and the Studio share.
//
// It used to live inside studio.ts, welded to satellite frames, while the
// cockpit had markup and no way out of the screen at all. A control map with
// arrows on it is exported exactly the way a before/after pair is: stage the
// map, let it settle, read the canvas back, crop, and compose a titled,
// captioned image with a legend and a scale bar.
//
// Reading the canvas back. The Studio's map is created with
// preserveDrawingBuffer; the cockpit's is not (keeping every frame costs
// memory and a slower present on the map everyone looks at all day). Both
// are read INSIDE MapLibre's `render` event instead: the WebGL drawing buffer
// is intact until the browser composites, which happens after the animation
// frame that fired the event returns — so one code path serves both maps.
//
// Everything on screen is WHAT IS EXPORTED — markup is MapLibre layers now,
// so it is in the canvas — and never a second rendering path that could
// disagree with the screen. The crop is the screen too ("on screen is good
// enough" — a sheet's frame is fitted to the view first, and a frame that
// still spills off the screen is cut to what is visible, and said so).

import type * as maplibregl from 'maplibre-gl';
import { encodeGif, type GifFrame } from './gif';
import { toast } from './hud';

export type Bbox = [number, number, number, number];
export interface Rect { x: number; y: number; w: number; h: number }
type Pt = { x: number; y: number };

export type AspectKey = 'free' | '16:9' | '4:3' | '1:1' | '3:4' | 'a4l' | 'a4p';

export const ASPECTS: Record<AspectKey, { label: string; ratio: number | null }> = {
  free: { label: 'Free', ratio: null },
  '16:9': { label: '16:9 slide', ratio: 16 / 9 },
  '4:3': { label: '4:3', ratio: 4 / 3 },
  '1:1': { label: 'Square', ratio: 1 },
  '3:4': { label: '3:4 portrait', ratio: 3 / 4 },
  a4l: { label: 'A4 landscape', ratio: 297 / 210 },
  a4p: { label: 'A4 portrait', ratio: 210 / 297 },
};

/** A GIF is 8-bit and uncompressed between frames: past this long edge it
 *  runs to tens of megabytes and minutes of quantising for no visible gain. */
export const GIF_MAX_EDGE = 1200;

/** The largest rectangle of `ratio` (w/h) centred inside `r`; `r` itself
 *  when the ratio is free. */
export function fitAspect(r: Rect, ratio: number | null): Rect {
  if (!ratio || r.w <= 0 || r.h <= 0) return { ...r };
  let w = r.w;
  let h = w / ratio;
  if (h > r.h) { h = r.h; w = h * ratio; }
  return { x: r.x + (r.w - w) / 2, y: r.y + (r.h - h) / 2, w, h };
}

/** The smallest rectangle of `ratio` (w/h) centred on `r` that holds all of
 *  it — a frame fitted AROUND something (the Studio's image) rather than
 *  inside it; `r` itself when the ratio is free. */
export function coverAspect(r: Rect, ratio: number | null): Rect {
  if (!ratio || r.w <= 0 || r.h <= 0) return { ...r };
  let w = r.w;
  let h = w / ratio;
  if (h < r.h) { h = r.h; w = h * ratio; }
  return { x: r.x + (r.w - w) / 2, y: r.y + (r.h - h) / 2, w, h };
}

/** A drag from `a` to `b` held to `ratio`: the anchor corner stays put and
 *  the rectangle grows toward the pointer, as big as the drag's larger side
 *  allows. */
export function aspectDrag(a: Pt, b: Pt, ratio: number | null): Rect {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let w = Math.abs(dx);
  let h = Math.abs(dy);
  if (ratio) {
    if (w / ratio >= h) h = w / ratio;
    else w = h * ratio;
  }
  return { x: dx < 0 ? a.x - w : a.x, y: dy < 0 ? a.y - h : a.y, w, h };
}

/** The screen rectangle a ground box covers: the bounds of its projected
 *  corners, so it is right on a rotated map too. */
export function screenRectOf(project: (ll: [number, number]) => Pt, bbox: Bbox): Rect {
  const pts = [project([bbox[0], bbox[1]]), project([bbox[2], bbox[1]]),
               project([bbox[2], bbox[3]]), project([bbox[0], bbox[3]])];
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/** A screen rectangle as ground: the bounds of its unprojected corners. */
export function bboxOfRect(unproject: (p: [number, number]) => { lng: number; lat: number },
                           r: Rect): Bbox {
  const lls = [unproject([r.x, r.y]), unproject([r.x + r.w, r.y]),
               unproject([r.x + r.w, r.y + r.h]), unproject([r.x, r.y + r.h])];
  const lons = lls.map((l) => l.lng);
  const lats = lls.map((l) => l.lat);
  return [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)];
}

/** A CSS-pixel rectangle on the map as a crop of its DEVICE-pixel canvas,
 *  clamped to the canvas. `clipped` says part of the rectangle was off it. */
export function deviceCrop(r: Rect, cssW: number, cssH: number, devW: number, devH: number):
{ crop: Rect; clipped: boolean } {
  const sx = cssW > 0 ? devW / cssW : 1;
  const sy = cssH > 0 ? devH / cssH : 1;
  const x0 = Math.max(0, Math.round(r.x * sx));
  const y0 = Math.max(0, Math.round(r.y * sy));
  const x1 = Math.min(devW, Math.round((r.x + r.w) * sx));
  const y1 = Math.min(devH, Math.round((r.y + r.h) * sy));
  const clipped = r.x < -0.5 || r.y < -0.5 || r.x + r.w > cssW + 0.5 || r.y + r.h > cssH + 0.5;
  return { crop: { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) }, clipped };
}

/** Ground metres per CSS pixel at a zoom and latitude (MapLibre's world is
 *  512 px wide at zoom 0). */
export function metersPerCssPx(zoom: number, lat: number): number {
  return (40_075_016.686 * Math.cos((lat * Math.PI) / 180)) / (512 * 2 ** zoom);
}

/** The longest round scale-bar length (1, 2 or 5 × 10ⁿ metres) that fits in
 *  `maxPx`, and its label. */
export function scaleBar(metersPerPx: number, maxPx: number): { px: number; label: string } {
  const maxM = metersPerPx * maxPx;
  if (!(maxM > 0) || !Number.isFinite(maxM)) return { px: 0, label: '' };
  const pow = 10 ** Math.floor(Math.log10(maxM));
  const step = [5, 2, 1].map((k) => k * pow).find((m) => m <= maxM) ?? pow;
  const label = step >= 1000 ? `${step / 1000} km` : `${step} m`;
  return { px: step / metersPerPx, label };
}

/** A file-name-safe version of a title. */
export function fileSlug(s: string): string {
  const out = s.normalize('NFKD').replace(/[^\w\s-]/g, '').trim()
    .replace(/[\s_]+/g, '-').replace(/-+/g, '-').toLowerCase().slice(0, 60);
  return out || 'map';
}

/** Split text into at most `maxLines` lines no wider than `maxW` by `measure`,
 *  the last one ellipsised if the text runs on. */
export function wrapText(text: string, maxW: number, maxLines: number,
                         measure: (s: string) => number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (let i = 0; i < words.length; i++) {
    const next = line ? `${line} ${words[i]}` : words[i]!;
    if (measure(next) <= maxW || !line) { line = next; continue; }
    lines.push(line);
    line = words[i]!;
    if (lines.length === maxLines) {
      let last = lines[maxLines - 1]!;
      while (last.length > 1 && measure(`${last}…`) > maxW) last = last.slice(0, -1);
      lines[maxLines - 1] = `${last}…`;
      return lines;
    }
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) return lines.slice(0, maxLines);
  // A single unbreakable run can still be too wide.
  return lines.map((l) => {
    let s = l;
    while (s.length > 1 && measure(s) > maxW) s = s.slice(0, -2) + '…';
    return s;
  });
}

// --- the map ---------------------------------------------------------------------

/** Wait for the map to finish painting after a change. `idle` also waits on
 *  any tiles the change pulled in; the timeout is the backstop for a map that
 *  never goes idle (a basemap tile that never arrives). */
export function mapSettled(map: maplibregl.Map, timeoutMs = 4000): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      map.off('idle', finish);      // or the loser leaks a listener per frame
      resolve();
    };
    map.once('idle', finish);
    map.triggerRepaint();
    setTimeout(finish, timeoutMs);
  });
}

/** Read a device-pixel crop of the map's canvas into a new canvas of
 *  `outW` × `outH`, inside the next render (see the header). */
export function captureMap(map: maplibregl.Map, crop: Rect, outW: number,
                           outH: number): Promise<HTMLCanvasElement> {
  return new Promise((resolve) => {
    const out = document.createElement('canvas');
    out.width = outW;
    out.height = outH;
    const ctx = out.getContext('2d')!;
    ctx.imageSmoothingQuality = 'high';
    let done = false;
    const grab = (): void => {
      if (done) return;
      done = true;
      map.off('render', grab);
      ctx.drawImage(map.getCanvas(), crop.x, crop.y, crop.w, crop.h, 0, 0, outW, outH);
      resolve(out);
    };
    map.on('render', grab);
    map.triggerRepaint();
  });
}

// --- composing ---------------------------------------------------------------------

export interface LegendEntry { label: string; color: string; hatch?: boolean }

/** A legend is at most this many rows: past it the card covers the map. */
export const LEGEND_MAX = 10;

/** The key a sheet is exported with: the author's own entries first (what
 *  their pen colours mean), then what the surface derives (territorial
 *  control, the change layer) minus anything the author already named. */
export function mergeLegend(custom: LegendEntry[], derived: LegendEntry[]): LegendEntry[] {
  const out: LegendEntry[] = [];
  const seen = new Set<string>();
  for (const e of [...custom, ...derived]) {
    const label = e.label.trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...e, label });
    if (out.length === LEGEND_MAX) break;
  }
  return out;
}

const wrapLng = (lng: number): number => ((((lng + 180) % 360) + 360) % 360) - 180;

function deg(v: number, pos: string, neg: string, dp: number): string {
  return `${Math.abs(v).toFixed(dp)}°${v >= 0 ? pos : neg}`;
}

function span(lo: number, hi: number, pos: string, neg: string): string {
  if (lo >= 0) return `${lo.toFixed(4)}–${hi.toFixed(4)}°${pos}`;
  if (hi < 0) return `${(-hi).toFixed(4)}–${(-lo).toFixed(4)}°${neg}`;
  return `${(-lo).toFixed(4)}°${neg}–${hi.toFixed(4)}°${pos}`;
}

/** Where an export is, in words a reader can type into anything: the centre
 *  to ~1 m and the span of the four corners to ~10 m, WGS 84. The corners are
 *  the image's own, unprojected, so a rotated frame still states the ground it
 *  shows. A TILTED view gets its centre only — the corners of a perspective
 *  view are not a box on the ground, and the far ones may be sky. */
export function coordLine(corners: { lng: number; lat: number }[],
                          centre: { lng: number; lat: number },
                          tilted = false): string {
  const c = `Centre ${deg(centre.lat, 'N', 'S', 5)} ${deg(wrapLng(centre.lng), 'E', 'W', 5)}`;
  if (tilted || !corners.length) return `${c} (WGS 84)`;
  const lats = corners.map((p) => p.lat);
  const lngs = corners.map((p) => wrapLng(p.lng));
  return `${c} · ${span(Math.min(...lats), Math.max(...lats), 'N', 'S')}, ${
    span(Math.min(...lngs), Math.max(...lngs), 'E', 'W')} (WGS 84)`;
}

/** How far a north arrow turns from straight up, clockwise in degrees: the
 *  map's bearing is how far north was turned AWAY from up. */
export function northArrowRotation(bearing: number): number {
  return (((-bearing) % 360) + 360) % 360;   // the outer % turns -0 into 0
}

export interface SheetDecor {
  /** Band above the map, when either is set. */
  title?: string | null;
  subtitle?: string | null;
  /** The provenance line under the map — dates, sources, attribution. */
  footer: string;
  /** Where the map is (coordLine), a second line under the footer when set. */
  coords?: string | null;
  legend?: LegendEntry[];
  /** Ground metres per OUTPUT pixel; draws a scale bar when set. */
  metersPerPx?: number | null;
  /** The map's bearing; draws a north arrow (upper right) when set. */
  northBearing?: number | null;
}

/** One exported frame: an optional title band, the map image with its legend
 *  and scale bar over the lower corners, and a footer line. The size depends
 *  only on the image and which parts are present — never on the words — so
 *  every frame of a GIF comes out the same size. */
export function composeSheet(image: HTMLCanvasElement, decor: SheetDecor): HTMLCanvasElement {
  const W = image.width;
  const k = Math.max(0.7, Math.min(2.2, W / 1000));
  const pad = Math.round(14 * k);
  const measureCtx = document.createElement('canvas').getContext('2d')!;
  const font = (weight: number, px: number): string => `${weight} ${Math.round(px)}px system-ui, sans-serif`;

  const titlePx = 22 * k;
  const subPx = 13.5 * k;
  const title = decor.title?.trim() || '';
  const subtitle = decor.subtitle?.trim() || '';
  measureCtx.font = font(400, subPx);
  const subLines = subtitle
    ? wrapText(subtitle, W - pad * 2, 3, (s) => measureCtx.measureText(s).width) : [];
  const bandH = title || subLines.length
    ? Math.round(pad * 1.6 + (title ? titlePx * 1.25 : 0) + subLines.length * subPx * 1.35)
    : 0;
  const footPx = Math.max(10, 12 * k);
  const footLines = [decor.footer, decor.coords ?? ''].filter((l) => l.trim());
  const footH = Math.round(footPx * (footLines.length > 1 ? 3.75 : 2.3));

  const out = document.createElement('canvas');
  out.width = W;
  out.height = bandH + image.height + footH;
  const ctx = out.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.textBaseline = 'alphabetic';

  if (bandH) {
    let y = pad;
    if (title) {
      ctx.fillStyle = '#12202c';
      ctx.font = font(650, titlePx);
      y += titlePx;
      let t = title;
      while (t.length > 1 && ctx.measureText(t).width > W - pad * 2) t = `${t.slice(0, -2)}…`;
      ctx.fillText(t, pad, y);
      y += titlePx * 0.25;
    }
    ctx.fillStyle = '#3d4b59';
    ctx.font = font(400, subPx);
    for (const line of subLines) {
      y += subPx * 1.35;
      ctx.fillText(line, pad, y - subPx * 0.3);
    }
  }

  ctx.drawImage(image, 0, bandH);

  // North arrow: upper right, over the map. Turned by the bearing, so a
  // rotated map still says which way is north.
  if (decor.northBearing !== null && decor.northBearing !== undefined) {
    const r = 15 * k;
    const cx = W - pad - r;
    const cy = bandH + pad + r;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((northArrowRotation(decor.northBearing) * Math.PI) / 180);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(18,32,44,0.25)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = '#12202c';
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.78);
    ctx.lineTo(r * 0.32, r * 0.3);
    ctx.lineTo(0, r * 0.08);
    ctx.lineTo(-r * 0.32, r * 0.3);
    ctx.closePath();
    ctx.fill();
    ctx.font = font(700, 9 * k);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('N', 0, r * 0.82);
    ctx.textAlign = 'left';
    ctx.restore();
  }

  // Legend: lower left, over the map, on a pale card.
  const legend = (decor.legend ?? []).slice(0, LEGEND_MAX);
  if (legend.length) {
    const rowPx = 12.5 * k;
    const rowH = rowPx * 1.55;
    const sw = rowPx * 1.05;
    ctx.font = font(500, rowPx);
    const labels = legend.map((e) => {
      let s = e.label;
      while (s.length > 1 && ctx.measureText(s).width > W * 0.4) s = `${s.slice(0, -2)}…`;
      return s;
    });
    const textW = Math.max(...labels.map((s) => ctx.measureText(s).width));
    const boxW = pad + sw + pad * 0.6 + textW + pad;
    const boxH = pad * 0.8 + legend.length * rowH + pad * 0.4;
    const bx = pad;
    const by = bandH + image.height - pad - boxH;
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fillRect(bx, by, boxW, boxH);
    ctx.strokeStyle = 'rgba(18,32,44,0.25)';
    ctx.lineWidth = 1;
    ctx.strokeRect(bx + 0.5, by + 0.5, boxW - 1, boxH - 1);
    legend.forEach((e, i) => {
      const cy = by + pad * 0.8 + i * rowH + rowH / 2;
      const sx = bx + pad;
      ctx.fillStyle = e.color;
      ctx.globalAlpha = 0.75;
      ctx.fillRect(sx, cy - sw / 2, sw, sw);
      ctx.globalAlpha = 1;
      if (e.hatch) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(sx, cy - sw / 2, sw, sw);
        ctx.clip();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = Math.max(1, k);
        for (let d = -sw; d < sw * 2; d += sw / 3) {
          ctx.moveTo(sx + d, cy + sw / 2);
          ctx.lineTo(sx + d + sw, cy - sw / 2);
        }
        ctx.stroke();
        ctx.restore();
      }
      ctx.fillStyle = '#12202c';
      ctx.textBaseline = 'middle';
      ctx.fillText(labels[i]!, sx + sw + pad * 0.6, cy);
      ctx.textBaseline = 'alphabetic';
    });
  }

  // Scale bar: lower right, over the map.
  if (decor.metersPerPx && decor.metersPerPx > 0) {
    const bar = scaleBar(decor.metersPerPx, W * 0.2);
    if (bar.px >= 8) {
      const px = 11.5 * k;
      const x1 = W - pad;
      const x0 = x1 - bar.px;
      const y = bandH + image.height - pad;
      ctx.lineCap = 'butt';
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 6 * k;
      ctx.beginPath(); ctx.moveTo(x0, y); ctx.lineTo(x1, y); ctx.stroke();
      ctx.strokeStyle = '#12202c';
      ctx.lineWidth = 2.5 * k;
      ctx.beginPath(); ctx.moveTo(x0, y); ctx.lineTo(x1, y); ctx.stroke();
      ctx.font = font(600, px);
      ctx.textAlign = 'right';
      ctx.lineWidth = 3.5 * k;
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.strokeText(bar.label, x1, y - 5 * k);
      ctx.fillStyle = '#12202c';
      ctx.fillText(bar.label, x1, y - 5 * k);
      ctx.textAlign = 'left';
    }
  }

  // Footer: the provenance line, then where the map is.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, bandH + image.height, W, footH);
  ctx.fillStyle = '#3d4b59';
  ctx.font = font(500, footPx);
  ctx.textBaseline = 'middle';
  const lineH = footPx * 1.45;
  let y = bandH + image.height + footH / 2 - (lineH * (footLines.length - 1)) / 2;
  for (const line of footLines) {
    let foot = line;
    while (foot.length > 12 && ctx.measureText(foot).width > W - pad * 2) foot = `${foot.slice(0, -4)}…`;
    ctx.fillText(foot, pad, y);
    y += lineH;
  }
  return out;
}

// --- saving ------------------------------------------------------------------------

export function download(blob: Blob, name: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
}

/** Download a PNG and put it on the clipboard where the browser allows. */
export async function savePng(canvas: HTMLCanvasElement, name: string): Promise<void> {
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
  if (!blob) { toast('Export failed.'); return; }
  download(blob, `${name}.png`);
  let copied = false;
  try {
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    copied = true;
  } catch { /* clipboard unavailable or denied */ }
  toast(copied ? 'PNG downloaded and copied to the clipboard.' : 'PNG downloaded.');
}

/** Encode equal-sized frames as a looping GIF and download it. */
export async function saveGif(frames: HTMLCanvasElement[], fps: number, name: string): Promise<void> {
  if (!frames.length) return;
  const width = frames[0]!.width;
  const height = frames[0]!.height;
  const gifFrames: GifFrame[] = frames.map((c) => ({
    data: c.getContext('2d')!.getImageData(0, 0, width, height).data,
    delayMs: Math.round(1000 / Math.max(0.2, fps)),
  }));
  toast(`Encoding ${frames.length} frames…`);
  // Yield once so the toast paints before the encoder blocks the thread.
  await new Promise((r) => setTimeout(r, 30));
  let bytes: Uint8Array;
  try {
    bytes = encodeGif(gifFrames, width, height);
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not encode the GIF.');
    return;
  }
  const blob = new Blob([bytes as unknown as BlobPart], { type: 'image/gif' });
  download(blob, `${name}.gif`);
  toast(`GIF downloaded — ${frames.length} frames, ${(blob.size / 1_048_576).toFixed(1)} MB.`);
}
